'use client';

import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { forwardRef } from '@ironhack/design-system2/components/forward-ref';
import { Text, type TextProps } from '@ironhack/design-system2/components/text';
import { type SetRequired } from 'type-fest';
import { type ReactElement } from 'react';

import { Button } from '@/app/components/client/button';
import { StoryList } from '@/app/components/client/story-list';
import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import {
  type DatoButtonBlock,
  type DatoCompany,
  type DatoModuleFormat,
  type DatoStory,
} from '@/lib/datocms/types';
import { sendEvent } from '@/lib/gtm';

export type Story = Omit<DatoStory, 'company'> & {
  company: SetRequired<DatoCompany, 'horizontalBackgroundLogo'>;
};

export type Stories = {
  button?: [DatoButtonBlock];
  headingProps?: TextProps | null;
  moduleFormat: [DatoModuleFormat];
  stories: Story[];
  subtitle?: string;
  title?: string;
};

type Props = { data: Stories } & FlexProps;

export const Stories = forwardRef((props: Props, ref): ReactElement => {
  const { data, ...flexProps } = props;
  const {
    button: [button] = [],
    headingProps,
    moduleFormat: [moduleFormat],
    stories,
    subtitle,
    title,
  } = data;
  const hasTitleOrSubtitle = title || subtitle;
  const {
    pageData: { gaCategory },
  } = usePageContext<Record<string, unknown>, { gaCategory: string }>();

  return (
    <ModuleWrapper
      innerProps={{
        align: 'center',
        direction: 'column',
        textAlign: ['left', null, 'center'],
      }}
      moduleFormat={moduleFormat}
      outerProps={flexProps}
      ref={ref}
    >
      {hasTitleOrSubtitle && (
        <Flex direction="column" gap={2} mb={5} width="100%">
          {title && (
            <Text as="h2" textStyle={['3xl', null, '4xl']} {...headingProps}>
              {title}
            </Text>
          )}
          {subtitle && (
            <Text
              // Make sure it's 60% width in desktop in spite of outer padding
              mx={[0, null, 'calc(20% - var(--chakra-space-6))']}
              textStyle="l"
            >
              {subtitle}
            </Text>
          )}
        </Flex>
      )}
      <StoryList
        onStoryView={(userName) => {
          sendEvent({
            eventCategory: gaCategory,
            eventAction: 'play video alumni stories',
            eventLabel: userName,
          });
        }}
        stories={stories}
      />
      {button && <Button {...button} mt={4} />}
    </ModuleWrapper>
  );
});
